"use client"

import { useState } from "react"
import type { GameStats, LevelConfig } from "@/types/game.types"
import { WalletButton } from "@/components/wallet/wallet-button"
import { LivesDisplay } from "@/components/game-ui/hud/lives-display"
import { GoldDisplay } from "@/components/game-ui/hud/gold-display"
import { WaveDisplay } from "@/components/game-ui/hud/wave-display"
import { HamburgerMenuButton } from "@/components/game-ui/hud/hamburger-menu-button"
import { HUD_ICON_BUTTON_CLASS } from "@/components/game-ui/hud/hud-button-style"
import { PauseIcon } from "@/components/icons/pixel-icons"
import { GameMenu } from "@/components/game-ui/menu/game-menu"

interface GameHUDProps {
  stats: GameStats
  interestAnimation?: number | null
  sellAnimation?: number | null
  interestBonusPercent?: number
  level: LevelConfig
  canvasLeftOffset?: number
  isMobile?: boolean
  /** Current fast-forward rate (1 or 2) */
  gameSpeed?: number
  /** Toggle between 1x and 2x game speed */
  onToggleGameSpeed?: () => void
  /** Open the pause menu */
  onPause?: () => void
}

export const GameHUD = ({ stats, interestAnimation, sellAnimation, interestBonusPercent, level, canvasLeftOffset = 0, isMobile = false, gameSpeed = 1, onToggleGameSpeed, onPause }: GameHUDProps) => {
  const totalWaves = level.waves.length
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  return (
    <>
      <div
        id="game-hud-left"
        className="absolute top-4 flex flex-col gap-2 z-20"
        style={{ left: `${canvasLeftOffset + 16}px` }}
      >
        <div className="flex gap-2">
          <LivesDisplay lives={stats.lives} />
          <GoldDisplay gold={stats.money} interestAnimation={interestAnimation} sellAnimation={sellAnimation} interestBonusPercent={interestBonusPercent} />
        </div>

        <WaveDisplay wave={stats.wave} totalWaves={totalWaves} score={stats.score} />
      </div>

      {/* Wallet + codex menu buttons - mirrored against the right edge of the
          game area, not the window, so they sit inside the pillarbox like the
          lives/gold/wave chips on the left instead of floating over the
          background art. */}
      <div
        id="game-hud-right"
        className="absolute top-4 z-20 flex items-center gap-2"
        style={{ right: `${canvasLeftOffset + 16}px` }}
      >
        {onPause && (
          <button
            id="pause-button"
            onClick={onPause}
            className={HUD_ICON_BUTTON_CLASS}
            title="Pause"
            aria-label="Pause game"
          >
            <PauseIcon size={18} />
          </button>
        )}
        {onToggleGameSpeed && (
          <button
            id="game-speed-button"
            onClick={onToggleGameSpeed}
            className={`${HUD_ICON_BUTTON_CLASS} font-bold text-xs ${
              gameSpeed > 1 ? "text-amber-300" : "text-white"
            }`}
            title={gameSpeed > 1 ? "Fast forward on - back to normal speed" : "Fast forward (2x)"}
            aria-label={gameSpeed > 1 ? "Set normal game speed" : "Set double game speed"}
            aria-pressed={gameSpeed > 1}
          >
            {gameSpeed > 1 ? "2x" : "1x"}
          </button>
        )}
        <div className="relative">
          <WalletButton />
        </div>
        <HamburgerMenuButton onClick={() => setIsMenuOpen(true)} />
      </div>

      {/* Main menu */}
      <GameMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        isMobile={isMobile}
      />
    </>
  )
}
